'use client';

import React, { Fragment, useRef } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { HiChevronDown } from 'react-icons/hi';
import { MdOutlineCheck } from 'react-icons/md';
import { cx } from 'cva';
import { switchUserCommunity } from '@/actions/switch-community.action';
import { NEKI_LOGO_S3_URL } from '@/constants/neki.constant';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import Typography from '@ui/Typography';
import Avatar from '../next-ui/Avatar';
import { useBreakpointHooks } from '@/utils/break-points.provider';

function CommunitySelector({
  communities,
  activeCommunityId,
  activeCommunity,
}) {
  const isSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('md'));

  // Selected Option (either a community or a group)
  const selectedOptionRef = useRef(
    communities
      .flatMap((c) => [c, ...(c.groups || [])])
      .find((c) => c.id === activeCommunityId),
  );
  const selectedOption = selectedOptionRef.current;

  const getCommunityLogoUrl = (entity) => {
    return entity.logoUrl
      ? getQualifiedImageUrl(entity.logoUrl)
      : NEKI_LOGO_S3_URL;
  };

  const handleCommunityChange = async (opt) => {
    if (activeCommunityId !== opt.id) {
      switchUserCommunity(opt.id);
    }
  };

  // Main block to render:
  const primaryCommunity = activeCommunity?.primaryCommunity
    ? communities.find((c) => c.id === activeCommunity.primaryCommunity.id)
    : null;

  const mainCommunityToRender =
    selectedOption?.communityId && primaryCommunity
      ? primaryCommunity
      : communities.find(
          (c) =>
            c.id === selectedOption?.id || c.id === selectedOption?.communityId,
        );

  const siblingGroups = mainCommunityToRender?.groups || [];

  const otherCommunities = communities.filter(
    (c) => c.id !== mainCommunityToRender?.id,
  );

  return (
    <Listbox value={selectedOption} onChange={handleCommunityChange}>
      <div className="relative">
        <div className="flex flex-col gap-1">
          <Listbox.Button
            className={cx(
              'focus:shadow-outline relative flex w-full cursor-pointer items-center justify-start gap-2 rounded-lg bg-primary-light-1 px-3 py-2 text-left focus:outline-none md:px-6 md:py-3',
            )}>
            {selectedOption && (
              <div
                className={cx(
                  'flex items-center justify-start gap-2',
                  isSmallScreen && 'w-[160px]',
                )}>
                <Typography variant="h5" component="span" className="truncate">
                  {selectedOption.name}
                </Typography>
              </div>
            )}
            <span className="">
              <HiChevronDown
                className="h-5 w-5 text-text-secondary"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
        </div>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <Listbox.Options
            className={cx(
              'sm:text-sm absolute z-3000 mt-1 max-h-80 w-max overflow-auto rounded-md border border-[rgba(0,0,0,0.1)] bg-white py-1 text-base shadow-lg focus:outline-none ',
              isSmallScreen && 'max-h-60 max-w-[230px]',
            )}>
            {/* Main Community */}
            {mainCommunityToRender && (
              <Fragment key={mainCommunityToRender.id}>
                <Listbox.Option
                  as="div"
                  role="option"
                  tabIndex={0}
                  className={({ active }) =>
                    cx(
                      'relative cursor-pointer select-none py-2 pl-10 pr-4',
                      active && 'bg-primary-light-1',
                    )
                  }
                  value={mainCommunityToRender}>
                  {({ selected }) => (
                    <>
                      <div className="flex items-center gap-2">
                        <Avatar
                          src={getCommunityLogoUrl(mainCommunityToRender)}
                          alt={mainCommunityToRender.name}
                          profile="company"
                          size="xs"
                        />
                        <Typography
                          variant="h5"
                          component="span"
                          color={selected ? 'textPrimary' : 'textSecondary'}
                          className="truncate">
                          {mainCommunityToRender.name}
                        </Typography>
                      </div>
                      {selected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text-secondary">
                          <MdOutlineCheck
                            className="h-5 w-5"
                            aria-hidden="true"
                          />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>

                {/* Groups */}
                {siblingGroups.length > 0 && (
                  <>
                    <hr className="my-1 border-gray-200" />
                    <Typography
                      variant="body2"
                      className="text-sm pb-1 pl-14 font-semibold uppercase text-gray-500">
                      Groups
                    </Typography>
                    {siblingGroups.map((group) => (
                      <Listbox.Option
                        as="div"
                        role="option"
                        tabIndex={0}
                        key={group.id}
                        className={({ active }) =>
                          cx(
                            'relative cursor-pointer select-none py-2 pl-14 pr-4',
                            active && 'bg-primary-light-1',
                          )
                        }
                        value={group}>
                        {({ selected }) => (
                          <>
                            <div className="flex items-center gap-2">
                              <Avatar
                                src={getCommunityLogoUrl(group)}
                                alt={group.name}
                                profile="company"
                                size="xs"
                              />
                              <Typography
                                variant="h5"
                                component="span"
                                color={
                                  selected ? 'textPrimary' : 'textSecondary'
                                }
                                className="truncate">
                                {group.name}
                              </Typography>
                            </div>
                            {selected && (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text-secondary">
                                <MdOutlineCheck
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </>
                )}
              </Fragment>
            )}

            {/* Other Communities */}
            {otherCommunities.length > 0 && (
              <>
                <hr className="my-2 border-gray-300" />
                <Typography
                  variant="body2"
                  className="text-sm pb-1 pl-10 font-semibold uppercase text-gray-500">
                  My Communities
                </Typography>
                {otherCommunities.map((community) => (
                  <Listbox.Option
                    as="div"
                    role="option"
                    tabIndex={0}
                    key={community.id}
                    className={({ active }) =>
                      cx(
                        'relative cursor-pointer select-none py-2 pl-10 pr-4',
                        active && 'bg-primary-light-1',
                      )
                    }
                    value={community}>
                    {({ selected }) => (
                      <>
                        <div className="flex items-center gap-2">
                          <Avatar
                            src={getCommunityLogoUrl(community)}
                            alt={community.name}
                            profile="company"
                            size="xs"
                          />
                          <Typography
                            variant="h5"
                            component="span"
                            color={selected ? 'textPrimary' : 'textSecondary'}
                            className="truncate">
                            {community.name}
                          </Typography>
                        </div>
                        {selected && (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-text-secondary">
                            <MdOutlineCheck
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          </span>
                        )}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </>
            )}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

export default React.memo(CommunitySelector);
